import { useState } from "react";
import {
  Filter as F,
  FilterValue,
  useQueryBuilder as QueryBuilder,
} from "@cgarciagarcia/react-query-builder";
import type { SortingState } from "@tanstack/react-table";
import { useSearchParams } from "react-router-dom";

import { env } from "~/env";

interface Filter<K extends string> {
  attribute: K;
  value: FilterValue;
}

export const usersFilters = {
  search: "search",
  role: "role",
} as const;

export const providersFilters = {
  search: "search",
  hasSchedule: "hasSchedule",
} as const;

export const bannersFilters = {
  search: "search",
} as const;

export const insuranceProvidersFilters = {
  providerName: "providerName",
  insuranceName: "insuranceName",
  stateName: "stateName",
} as const;

export const userEventsFilters = {
  search: "search",
  eventType: "eventType",
  outcome: "outcome",
  dateFrom: "dateFrom",
  dateTo: "dateTo",
} as const;

interface UseQueryBuilderInterface<T extends Record<string, string>> {
  builderFilters?: F<T>[];
  aliases: T;
}

type BuilderAttribute<T> = T extends object
  ? string | (keyof T & string)
  : string;

export const useQueryBuilder = <T extends Record<string, string>>({
  builderFilters,
  aliases,
}: UseQueryBuilderInterface<T>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortingState, setSortingState] = useState<SortingState>([]);
  const [_, setFilterState] = useState<Filter<keyof T & string>[]>();

  const params = new URLSearchParams(searchParams);

  const builder = QueryBuilder<T>({
    useQuestionMark: true,
    pagination: {
      page: Number(searchParams.get("page")) || 1,
      limit:
        Number(searchParams.get("limit")) || env.VITE_PAGINATION_DEFAULT_SIZE,
    },
    filters: builderFilters,
    aliases,
  });

  const setCurrentPage = (page: number) => {
    builder.page(page);

    params.set("page", page.toString());
    setSearchParams(params);
  };

  const setSorting = (sorting: SortingState) => {
    builder.clearSorts();

    if (sorting.length > 0) {
      sorting.forEach(({ id, desc }) => {
        builder.sort(id as BuilderAttribute<T>, desc ? "desc" : "asc");
      });
    }

    setSortingState(sorting);

    if (sorting.length > 0 && sorting[0]) {
      params.set("orderBy", sorting[0].id);
      params.set("sortDirection", sorting[0].desc ? "desc" : "asc");
    } else {
      params.delete("orderBy");
      params.delete("sortDirection");
    }
    setSearchParams(params);
  };

  const setFilters = (filters: Filter<keyof T & string>[]) => {
    if (filters.length > 0) {
      filters.forEach((filter) => {
        const { attribute, value } = filter;

        builder.removeFilter(attribute as BuilderAttribute<T>);

        if (filterShouldBeApplied(filter)) {
          builder.filter(attribute as BuilderAttribute<T>, value);

          params.set(attribute, value.toString());
        } else {
          params.delete(attribute);
        }
      });
    }

    setFilterState(filters);
    setSearchParams(params);
  };

  const filterShouldBeApplied = (filter: Filter<keyof T & string>) => {
    if (Array.isArray(filter.value)) {
      const values = filter.value.filter((value: any) => value);

      return values.length > 0;
    }

    return !!filter.value;
  };

  return {
    builder,
    setCurrentPage,
    setSorting,
    sortingState,
    setFilters,
  };
};
